import React, { useState } from "react";
import { Users, Plus, Minus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { subscriptionApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { apiError } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// Seat billing — 1 seat included with the plan, each extra active staff
// member is £5/month (or £50/year to match an annual plan). Managed on the
// company's Stripe subscription with proration.
export default function SeatsCard({ isAdmin }) {
  const { data, loading, reload } = useAsync(() => subscriptionApi.seats(), []);
  const [pending, setPending] = useState(null); // desired seat count being confirmed
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  if (loading) return null;
  const s = data?.data || data || {};
  const used = s.seats_used ?? 0;
  const total = s.max_seats; // null = unlimited (legacy/store)
  const per = s.interval === "year" ? "£50/year" : "£5/month";
  const unit = s.interval === "year" ? 50 : 5;
  const canManage = isAdmin && s.can_manage && s.stripe_managed;

  async function openAdd() {
    const desired = (total || 1) + 1;
    setPending(desired);
    setPreview(null);
    try {
      const res = await subscriptionApi.seatsPreview(desired);
      setPreview(res?.data || res);
    } catch { /* preview is best-effort */ }
  }

  async function confirmChange() {
    setSaving(true);
    try {
      await subscriptionApi.updateSeats(pending);
      toast.success("Seats updated — your subscription has been adjusted.");
      setPending(null);
      reload();
    } catch (e) {
      toast.error(apiError(e, "Could not update seats"));
    } finally {
      setSaving(false);
    }
  }

  async function removeSeat() {
    const desired = Math.max(1, (total || 1) - 1);
    if (desired < used) {
      toast.error(`You have ${used} active members. Remove a member before reducing seats.`);
      return;
    }
    setSaving(true);
    try {
      await subscriptionApi.updateSeats(desired);
      toast.success("Seat removed — your next bill will be lower.");
      reload();
    } catch (e) {
      toast.error(apiError(e, "Could not update seats"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="p-5" data-testid="subscription-seats">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Users className="mt-0.5 h-5 w-5 shrink-0 text-slate-700" />
          <div>
            <p className="font-semibold text-slate-800">Team seats</p>
            <p className="mt-0.5 text-sm text-slate-600" data-testid="seats-usage">
              {used} {used === 1 ? "member" : "members"}
              {total == null ? "" : ` · ${total} seat${total === 1 ? "" : "s"}`} in use
            </p>
            <p className="mt-1 text-xs text-slate-500">
              1 seat included with your plan, then {per} per extra team member (admins, inspectors & maintenance). Client portal logins are free.
            </p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              disabled={saving || total == null || (total || 1) <= Math.max(1, used)}
              onClick={removeSeat} data-testid="seats-remove"
              title="Remove a seat"
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="min-w-[1.5rem] text-center text-sm font-semibold text-slate-800" data-testid="seats-total">
              {total == null ? "∞" : total}
            </span>
            <Button
              variant="outline" size="icon" className="h-8 w-8"
              disabled={saving} onClick={openAdd} data-testid="seats-add"
              title="Add a seat"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {isAdmin && !s.stripe_managed && total != null && (
        <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="seats-subscribe-hint">
          Subscribe by card below to add paid team seats. On the free trial only the account owner can sign in.
        </p>
      )}

      <Dialog open={pending != null} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent data-testid="seats-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Add a team seat?</DialogTitle>
            <DialogDescription>
              This increases your subscription by {per}.{" "}
              {preview && preview.amount_due_now != null
                ? `You'll be charged about £${Number(preview.amount_due_now).toFixed(2)} now for the rest of your current billing period.`
                : `A prorated amount for the rest of your current billing period will be charged now.`}
              {" "}It then renews with your plan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={saving} data-testid="seats-confirm-cancel">Cancel</Button>
            <Button onClick={confirmChange} disabled={saving} data-testid="seats-confirm-add">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}
              Add seat ({per})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
